import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import './Arrived.css';

interface ProgramInterestCamper {
  id: string;
  registrationId: string;
  camperName: string;
  placeCode: string;
  status: string;
  programs: string[];
}

const ProgramInterests: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campers, setCampers] = useState<ProgramInterestCamper[]>([]);
  const [filterProgram, setFilterProgram] = useState<string>('all');

  useEffect(() => {
    const fetchProgramInterests = async () => {
      try {
        setLoading(true);
        setError(null);

        const registrationsRef = collection(db, 'Registrations');
        const snapshot = await getDocs(registrationsRef);
        
        const camperData: ProgramInterestCamper[] = [];
        
        snapshot.forEach(doc => {
          const data = doc.data();
          const attendingCampers = data.attendingCampers || [];
          
          // Get place code from various possible locations
          const placeCode = data.financial?.accommodationFees?.placeCode || 
                           data.assignedAccommodation?.placeCode || 
                           data.accommodationFees?.placeCode || 
                           'Not Assigned';
          
          // Get registration status
          const status = data.status || 'unknown';
          
          attendingCampers.forEach((camper: any, index: number) => {
            const interests = camper.programInterests;
            
            // Check if any program interest is checked
            if (interests) {
              const checkedPrograms: string[] = [];
              
              if (interests.nurseryProgram) checkedPrograms.push('Nursery Program');
              if (interests.vbsProgram) checkedPrograms.push('VBS Program');
              if (interests.youthGroup) checkedPrograms.push('Youth Group');
              if (interests.cruiseProgram) checkedPrograms.push('Cruise Program');
              if (interests.volunteerInterest) checkedPrograms.push('Volunteer Interest');
              
              // Only add if at least one program is checked
              if (checkedPrograms.length > 0) {
                camperData.push({
                  id: `${doc.id}-${index}`,
                  registrationId: doc.id,
                  camperName: `${camper.firstName || ''} ${camper.lastName || ''}`.trim(),
                  placeCode,
                  status,
                  programs: checkedPrograms
                });
              }
            }
          });
        });

        // Sort by camper name
        camperData.sort((a, b) => a.camperName.localeCompare(b.camperName));
        
        setCampers(camperData);
      } catch (err) {
        console.error('Error fetching program interests:', err);
        setError('Failed to load program interests data');
      } finally {
        setLoading(false);
      }
    };

    fetchProgramInterests();
  }, []);

  const filteredCampers = filterProgram === 'all' 
    ? campers 
    : campers.filter(camper => camper.programs.includes(filterProgram));

  const exportToCSV = () => {
    const headers = ['Camper Name', 'Place Code', 'Status', 'Programs'];
    
    const csvRows = [
      headers.join(','),
      ...filteredCampers.map(camper => [
        `"${camper.camperName}"`,
        `"${camper.placeCode}"`,
        `"${camper.status}"`,
        `"${camper.programs.join('; ')}"`
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `program-interests-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'hold':
        return 'status-hold';
      case 'assigned':
        return 'status-assigned';
      case 'pending':
        return 'status-pending';
      default:
        return 'status-unknown';
    }
  };

  if (loading) {
    return <div className="arrived-report loading">Loading program interests data...</div>;
  }

  if (error) {
    return <div className="arrived-report error">{error}</div>;
  }

  return (
    <div className="arrived-report">
      <h2>Program Interests Report</h2>
      
      <div className="summary-section">
        <div className="summary-stats">
          <span className="total-sites">Total Campers with Interests: {campers.length}</span>
          <span className="arrived-count">Showing: {filteredCampers.length}</span>
        </div>
        
        <div className="view-toggle">
          <select 
            value={filterProgram} 
            onChange={(e) => setFilterProgram(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="all">All Programs</option>
            <option value="Nursery Program">Nursery Program</option>
            <option value="VBS Program">VBS Program</option>
            <option value="Youth Group">Youth Group</option>
            <option value="Cruise Program">Cruise Program</option>
            <option value="Volunteer Interest">Volunteer Interest</option>
          </select>
        </div>
      </div>

      <div className="campers-section">
        <div className="section-header">
          <h3>Campers with Program Interests</h3>
          {filteredCampers.length > 0 && (
            <button 
              className="export-csv-btn"
              onClick={exportToCSV}
            >
              Export to CSV
            </button>
          )}
        </div>
        
        {filteredCampers.length === 0 ? (
          <div className="no-campers">
            No campers found with program interests.
          </div>
        ) : (
          <table className="campers-table">
            <thead>
              <tr>
                <th>Camper Name</th>
                <th>Place Code</th>
                <th>Status</th>
                <th>Programs</th>
              </tr>
            </thead>
            <tbody>
              {filteredCampers.map(camper => (
                <tr key={camper.id}>
                  <td className="camper-name">
                    {camper.camperName}
                  </td>
                  <td className="place-name">
                    {camper.placeCode}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(camper.status)}`}>
                      {camper.status}
                    </span>
                  </td>
                  <td>
                    {camper.programs.map((program, idx) => (
                      <span 
                        key={idx} 
                        style={{ 
                          display: 'inline-block',
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          marginRight: '4px',
                          marginBottom: '2px'
                        }}
                      >
                        {program}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProgramInterests;

